/**
 * Document Renderer - For Creative / Text Agents
 * Used when: output.markdown exists AND no higher-priority renderer matched
 * 
 * Features:
 * - Clean document-style rendering
 * - Typography hierarchy (headings, bullets, spacing)
 * - NO collapsible sections
 * - Actions: Copy, Export (PDF/DOCX), Send to Tray
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Copy,
  DownloadSimple,
  FileText,
  PaperPlaneRight,
  Tray
} from '@phosphor-icons/react';

export const DocumentRenderer = ({ content, outputData, onAction }) => {
  const markdown = content?.markdown || '';
  
  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      onAction?.('copied');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  if (!markdown) {
    return (
      <div className="p-6 text-center">
        <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No document content available</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Document container */}
      <div className="bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden">
        {/* Document header */}
        <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-gray-400" />
            <span className="text-sm font-medium text-white">Document</span>
          </div>
          
          {/* Quick actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleCopy}
              className="p-2 text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
        
        {/* Document content - clean typography */}
        <div className="p-6">
          <article className="prose prose-invert max-w-none
            prose-headings:text-white prose-headings:font-semibold
            prose-h1:text-2xl prose-h1:mt-0 prose-h1:mb-6
            prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
            prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
            prose-h4:text-base prose-h4:mt-4 prose-h4:mb-2
            prose-p:text-gray-300 prose-p:leading-relaxed prose-p:my-4
            prose-li:text-gray-300 prose-li:my-2
            prose-ul:my-4 prose-ol:my-4
            prose-strong:text-white prose-strong:font-semibold
            prose-em:text-gray-200 prose-em:italic
            prose-blockquote:border-l-[#6366F1] prose-blockquote:bg-[rgba(99,102,241,0.1)] prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:text-gray-300 prose-blockquote:not-italic
            prose-code:bg-[rgba(255,255,255,0.1)] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[#6366F1] prose-code:font-mono prose-code:text-sm
            prose-pre:bg-[rgba(0,0,0,0.3)] prose-pre:rounded-lg prose-pre:p-4
            prose-a:text-[#6366F1] prose-a:no-underline hover:prose-a:underline
            prose-hr:border-[rgba(255,255,255,0.1)] prose-hr:my-8
            prose-table:border-collapse
            prose-th:bg-[rgba(255,255,255,0.05)] prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-medium prose-th:text-gray-300 prose-th:border prose-th:border-[rgba(255,255,255,0.1)]
            prose-td:px-4 prose-td:py-2 prose-td:border prose-td:border-[rgba(255,255,255,0.1)] prose-td:text-gray-300
          ">
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </article>
        </div>
      </div>
      
      {/* Action bar */}
      <div className="flex flex-wrap gap-2 pt-2">
        <button 
          onClick={handleCopy}
          className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <Copy size={14} />
          Copy
        </button>
        <button 
          onClick={() => onAction?.('export', 'pdf')}
          className="px-4 py-2 text-sm bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors flex items-center gap-2"
        >
          <DownloadSimple size={14} />
          Export PDF
        </button>
        <button 
          onClick={() => onAction?.('export', 'docx')}
          className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <FileText size={14} />
          Export DOCX
        </button>
        <button 
          onClick={() => onAction?.('send_to_inbox')}
          className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <Tray size={14} />
          Send to Tray
        </button>
      </div>
      
      {/* Document ready message */}
      <p className="text-xs text-gray-500 text-center">
        This document is ready for review and export.
      </p>
    </div>
  );
};

export default DocumentRenderer;
